import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import apiReports, {
  IOfferTimelineRow,
  IOfferTimelineWorkType,
} from 'api/reports';
import PageContainer from 'components/layout/PageContainer';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import getOfferStatusLabel from 'services/offers/offerStatusLabel';
import translations from 'services/translations/translations';

import 'style/reports/OfferDashboard.css';
import 'style/reports/OfferTimeline.css';

const DAY = 24 * 60 * 60 * 1000;

// The days the width of the timeline shows in each zoom: six weeks or six months
const VISIBLE_DAYS = { week: 42, month: 183 };

type Zoom = keyof typeof VISIBLE_DAYS;
type GroupBy = 'none' | 'department' | 'type';

const TYPE_ORDER = [
  'opportunity',
  'preliminary-offer',
  'offer',
  'material-offer',
  'cost-estimate',
];

// The colors of the work types: suspended ceilings yellow, dividing walls green, glass
// walls cyan, the others colors of their own
const WORK_TYPE_COLORS: { [group: string]: string } = {
  'suspended-ceilings-installed': '#f2c94c',
  'dividing-walls-installed': '#27ae60',
  'glass-walls-installed': '#00bcd4',
  'glass-ceilings-installed': '#4fa3f7',
  'glass-railings-installed': '#3f51b5',
  'balcony-glazings-installed': '#00838f',
  'facades-installed': '#9b51e0',
  'wall-claddings-installed': '#f2994a',
  'insulation-work-installed': '#e84393',
  'materials-delivered': '#8d6e63',
};

const OTHER_WORK_TYPE_COLOR = '#90a4ae';

// The background of a contract: the color of its work type, or the colors of its work
// types one after the other in the order of the positions, each as long a part of the
// contract as its share of the hours (equal parts when there are no hours)
const workTypeColors = (workTypes: IOfferTimelineWorkType[]): string => {
  if (workTypes.length === 0) {
    return OTHER_WORK_TYPE_COLOR;
  }

  const colorOf = (workType: IOfferTimelineWorkType) =>
    WORK_TYPE_COLORS[workType.group] ?? OTHER_WORK_TYPE_COLOR;
  if (workTypes.length === 1) {
    return colorOf(workTypes[0]);
  }

  const total = workTypes.reduce((sum, workType) => sum + workType.hours, 0);
  let start = 0;
  const parts = workTypes.map((workType) => {
    const share =
      total > 0 ? (workType.hours / total) * 100 : 100 / workTypes.length;
    const part = `${colorOf(workType)} ${start}% ${start + share}%`;
    start += share;
    return part;
  });
  return `linear-gradient(90deg, ${parts.join(', ')})`;
};

// Room before the first and after the last date
const MARGIN_DAYS = 21;

// Today is this many days from the left edge of the timeline
const LEAD_DAYS = 7;

// The current offers: from this many days ago to the end of the six months shown. The
// timeline shows only that period; the offers in it are shown, cut at its edges.
const CURRENT_DAYS_BACK = 90;

// The hours are written beside a bar narrower than this
const MIN_LABEL_WIDTH = 44;

type Period = 'current' | 'all';

// The day of a date as a number of days, from its calendar date wherever it is read
const dayOf = (value: Date): number =>
  Math.round(
    Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()) / DAY
  );

const dayOfText = (value: null | string): null | number =>
  value ? dayOf(new Date(value)) : null;

const dateOfDay = (day: number): Date => new Date(day * DAY);

// The ISO week number of a day
const weekOf = (day: number): number => {
  const date = dateOfDay(day);
  const weekday = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - weekday + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  return (
    1 +
    Math.round(
      ((date.getTime() - firstThursday.getTime()) / DAY -
        3 +
        ((firstThursday.getUTCDay() + 6) % 7)) /
        7
    )
  );
};

interface ITimelineOffer extends IOfferTimelineRow {
  // The contract: from its start to its end (both included); one of them when only one is given
  contractFrom: null | number;
  contractTo: null | number;
  siteFrom: null | number;
  siteTo: null | number;
}

interface IGroup {
  key: string;
  label: null | string;
  offers: ITimelineOffer[];
}

const OfferTimeline = (): JSX.Element => {
  const { t, i18n } = useTranslation([
    translations.offers,
    translations.products,
  ]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.offers}:timeline.${key}`, options);
  const history = useHistory();

  const [rows, setRows] = useState<IOfferTimelineRow[] | null>(null);
  const [zoom, setZoom] = useState<Zoom>('month');
  const [groupBy, setGroupBy] = useState<GroupBy>('department');
  const [department, setDepartment] = useState('');
  const [type, setType] = useState('');
  const [period, setPeriod] = useState<Period>('current');
  // The width of the timeline beside the names, followed when the window is resized
  const [timelineWidth, setTimelineWidth] = useState(1200);

  useEffect(() => {
    let cancelled = false;
    apiReports
      .getOfferTimeline()
      .then((result) => !cancelled && setRows(result))
      .catch(() => !cancelled && setRows([]));
    return () => {
      cancelled = true;
    };
  }, []);

  const offers: ITimelineOffer[] = useMemo(
    () =>
      (rows ?? []).map((row) => {
        const starts = dayOfText(row.workStarts);
        const ends = dayOfText(row.workEnds);
        return {
          ...row,
          contractFrom: starts ?? ends,
          contractTo: ends ?? starts,
          siteFrom: dayOfText(row.siteStarts),
          siteTo: dayOfText(row.siteEnds),
        };
      }),
    [rows]
  );

  const departments = useMemo(() => {
    const names = new Map<string, string>();
    offers.forEach((offer) =>
      names.set(
        String(offer.departmentId ?? ''),
        offer.departmentName ?? tr('No department')
      )
    );
    return Array.from(names.entries()).sort((a, b) => a[1].localeCompare(b[1]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers, i18n.language]);

  const types = useMemo(
    () =>
      TYPE_ORDER.filter((offerType) =>
        offers.some((offer) => offer.type === offerType)
      ),
    [offers]
  );

  // The last day of the contract or the site
  const endOf = (offer: ITimelineOffer) =>
    Math.max(
      offer.contractTo ?? -Infinity,
      offer.contractFrom ?? -Infinity,
      offer.siteTo ?? -Infinity,
      offer.siteFrom ?? -Infinity
    );

  const todayDay = dayOf(new Date());
  const currentFirstDay = todayDay - CURRENT_DAYS_BACK;
  // Six months from the left edge, where today is
  const currentLastDay = todayDay - LEAD_DAYS + VISIBLE_DAYS.month;

  // The first day of the contract or the site, the order of the rows in a group
  const startOf = (offer: ITimelineOffer) =>
    Math.min(
      offer.contractFrom ?? Infinity,
      offer.siteFrom ?? Infinity,
      offer.siteTo ?? Infinity
    );

  const shown = offers.filter(
    (offer) =>
      (department === '' || String(offer.departmentId ?? '') === department) &&
      (type === '' || offer.type === type) &&
      (period === 'all' ||
        (endOf(offer) >= currentFirstDay && startOf(offer) <= currentLastDay))
  );

  const getGroups = (): IGroup[] => {
    const ordered = [...shown].sort((a, b) => startOf(a) - startOf(b));
    if (groupBy === 'none') {
      return [{ key: 'all', label: null, offers: ordered }];
    }

    const byKey = new Map<string, IGroup>();
    ordered.forEach((offer) => {
      const key =
        groupBy === 'department'
          ? String(offer.departmentId ?? '')
          : offer.type;
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          label:
            groupBy === 'department'
              ? offer.departmentName ?? tr('No department')
              : t(`${translations.offers}:types.${offer.type}`),
          offers: [],
        });
      }
      byKey.get(key)?.offers.push(offer);
    });

    return Array.from(byKey.values()).sort((a, b) =>
      groupBy === 'type'
        ? TYPE_ORDER.indexOf(a.key) - TYPE_ORDER.indexOf(b.key)
        : (a.label ?? '').localeCompare(b.label ?? '')
    );
  };
  const groups = getGroups();

  // The days of the timeline: the dates of the offers shown and today, with some room
  const allDays = shown
    .flatMap((offer) => [
      offer.contractFrom,
      offer.contractTo,
      offer.siteFrom,
      offer.siteTo,
    ])
    .filter((day): day is number => day !== null);
  const firstDay =
    period === 'current'
      ? currentFirstDay
      : Math.min(todayDay, ...allDays) - MARGIN_DAYS;
  const lastDay =
    period === 'current'
      ? currentLastDay
      : Math.max(currentLastDay, ...allDays) + MARGIN_DAYS;
  // The timeline fills the page: its width shows six weeks or six months
  const dayWidth = timelineWidth / VISIBLE_DAYS[zoom];
  const width = (lastDay - firstDay + 1) * dayWidth;
  const x = (day: number) => (day - firstDay) * dayWidth;

  // The scales: months and weeks in the week zoom, years and months in the month zoom
  const scales = useMemo(() => {
    const months: { day: number; label: string }[] = [];
    const years: { day: number; label: string }[] = [];
    const weeks: { day: number; label: string }[] = [];
    const monthName = new Intl.DateTimeFormat(i18n.language, {
      month: zoom === 'week' ? 'long' : 'short',
      year: zoom === 'week' ? 'numeric' : undefined,
    });

    for (let day = firstDay; day <= lastDay; day += 1) {
      const date = dateOfDay(day);
      if (date.getUTCDate() === 1 || day === firstDay) {
        months.push({ day, label: monthName.format(date) });
      }
      if (
        (date.getUTCMonth() === 0 && date.getUTCDate() === 1) ||
        day === firstDay
      ) {
        years.push({ day, label: String(date.getUTCFullYear()) });
      }
      if (date.getUTCDay() === 1) {
        weeks.push({ day, label: `${tr('Week short')} ${weekOf(day)}` });
      }
    }

    return { months, weeks, years };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstDay, lastDay, zoom, i18n.language]);

  const upper = zoom === 'week' ? scales.months : scales.years;
  const lower = zoom === 'week' ? scales.weeks : scales.months;

  // Dragging the timeline sideways; a drag is not a click on an offer
  const scroller = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; left: number; moved: boolean } | null>(null);

  const onMouseDown = (event: React.MouseEvent) => {
    if (event.button !== 0 || !scroller.current) {
      return;
    }
    drag.current = {
      x: event.clientX,
      left: scroller.current.scrollLeft,
      moved: false,
    };
  };

  useEffect(() => {
    const onMove = (event: MouseEvent) => {
      if (!drag.current || !scroller.current) {
        return;
      }
      const moved = event.clientX - drag.current.x;
      if (Math.abs(moved) > 3) {
        drag.current.moved = true;
        scroller.current.classList.add('dragging');
      }
      scroller.current.scrollLeft = drag.current.left - moved;
    };
    const onUp = () => {
      scroller.current?.classList.remove('dragging');
      // The click after a drag is ignored, then the drag ends
      setTimeout(() => {
        drag.current = null;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // The width of the timeline when it is shown and when the window is resized
  const hasRows = shown.length > 0;
  useLayoutEffect(() => {
    const measure = () => {
      if (scroller.current && scroller.current.clientWidth > 0) {
        setTimelineWidth(scroller.current.clientWidth);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [hasRows]);

  // Today near the left edge when the timeline opens or the zoom or width changes
  useLayoutEffect(() => {
    if (scroller.current) {
      scroller.current.scrollLeft = Math.max(0, x(todayDay - LEAD_DAYS));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, rows, period, timelineWidth, hasRows]);

  const open = (offer: ITimelineOffer) => {
    if (!drag.current?.moved) {
      history.push(`/offers/${offer.id}/${offer.type}`);
    }
  };

  const formatDay = (day: null | number) =>
    day === null
      ? '–'
      : dateOfDay(day).toLocaleDateString(i18n.language, { timeZone: 'UTC' });

  const hoursText = (hours: null | number) =>
    hours ? `${formatGroupedNumber(hours, 0, 0)} h` : '';

  const titleOf = (offer: ITimelineOffer) =>
    [
      offer.targetName,
      `${t(
        `${translations.offers}:types.${offer.type}`
      )}, ${getOfferStatusLabel(t, offer.status, offer.type)}`,
      `${tr('Contract')}: ${formatDay(offer.contractFrom)} – ${formatDay(
        offer.contractTo
      )}`,
      `${tr('Site starts')}: ${formatDay(offer.siteFrom)}`,
      `${tr('Site ends')}: ${formatDay(offer.siteTo)}`,
      hoursText(offer.hours),
      // The hours of each work type when there are several
      ...(offer.workTypes.length > 1
        ? offer.workTypes.map(
            (workType) =>
              `${
                workType.group
                  ? t(`${translations.products}:groups.${workType.group}`)
                  : '–'
              }: ${hoursText(workType.hours) || '0 h'}`
          )
        : []),
    ]
      .filter(Boolean)
      .join('\n');

  // The contract as a bar with its hours; beside a bar too narrow for them
  const renderBar = (offer: ITimelineOffer, from: number, to: number) => {
    const left = x(Math.min(from, to));
    const barWidth = Math.max(dayWidth, (Math.abs(to - from) + 1) * dayWidth);
    const hours = hoursText(offer.hours);
    const inside = barWidth >= MIN_LABEL_WIDTH;

    return (
      <>
        <button
          className="offer-timeline-bar"
          onClick={() => open(offer)}
          style={{
            background: workTypeColors(offer.workTypes),
            left,
            width: barWidth,
          }}
          title={titleOf(offer)}
          type="button"
        >
          {inside ? hours : null}
        </button>
        {!inside && hours ? (
          <span
            className="offer-timeline-hours"
            style={{ left: left + barWidth + 4 }}
          >
            {hours}
          </span>
        ) : null}
      </>
    );
  };

  const segments = <T extends string>(
    value: T,
    choices: { key: T; label: string }[],
    onChange: (key: T) => void
  ) => (
    <div className="offer-dashboard-segments">
      {choices.map((choice) => (
        <button
          className={choice.key === value ? 'active' : undefined}
          key={choice.key}
          onClick={() => onChange(choice.key)}
          type="button"
        >
          {choice.label}
        </button>
      ))}
    </div>
  );

  return (
    <PageContainer fullWidth>
      <div className="offer-dashboard offer-timeline">
        <div className="offer-dashboard-header">
          <div>
            <h1>{tr('Title')}</h1>
            <p className="offer-dashboard-subtitle">{tr('Subtitle')}</p>
          </div>
          <div className="offer-dashboard-filters">
            <Input
              aria-label={tr('Department')}
              onChange={(event) => setDepartment(event.target.value)}
              type="select"
              value={department}
            >
              <option value="">{tr('All departments')}</option>
              {departments.map(([key, name]) => (
                <option key={key} value={key}>
                  {name}
                </option>
              ))}
            </Input>
            <Input
              aria-label={tr('Type')}
              onChange={(event) => setType(event.target.value)}
              type="select"
              value={type}
            >
              <option value="">{tr('All types')}</option>
              {types.map((offerType) => (
                <option key={offerType} value={offerType}>
                  {t(`${translations.offers}:types.${offerType}`)}
                </option>
              ))}
            </Input>
            {segments<Period>(
              period,
              [
                { key: 'current', label: tr('Current') },
                { key: 'all', label: tr('All') },
              ],
              setPeriod
            )}
            <span className="offer-timeline-label">{tr('Group by')}</span>
            {segments<GroupBy>(
              groupBy,
              [
                { key: 'department', label: tr('Department') },
                { key: 'type', label: tr('Type') },
                { key: 'none', label: tr('No grouping') },
              ],
              setGroupBy
            )}
            <span className="offer-timeline-label">{tr('Zoom')}</span>
            {segments<Zoom>(
              zoom,
              [
                { key: 'week', label: tr('Week') },
                { key: 'month', label: tr('Month') },
              ],
              setZoom
            )}
          </div>
        </div>

        <div className="offer-timeline-legend">
          <span>
            <i
              className="legend-bar"
              style={{
                background: workTypeColors([
                  { group: 'suspended-ceilings-installed', hours: 2 },
                  { group: 'dividing-walls-installed', hours: 1 },
                ]),
              }}
            />
            {tr('Contract')}
          </span>
          <span>
            <i className="legend-point site-start" />
            {tr('Site starts')}
          </span>
          <span>
            <i className="legend-point site-end" />
            {tr('Site ends')}
          </span>
          <span>
            <i className="legend-today" />
            {tr('Today')}
          </span>
          {/* The colors of the work types of the offers shown */}
          {Object.keys(WORK_TYPE_COLORS)
            .filter((group) =>
              shown.some((offer) =>
                offer.workTypes.some((workType) => workType.group === group)
              )
            )
            .map((group) => (
              <span key={group}>
                <i
                  className="legend-bar"
                  style={{ background: WORK_TYPE_COLORS[group] }}
                />
                {t(`${translations.products}:groups.${group}`)}
              </span>
            ))}
        </div>

        {rows !== null && shown.length === 0 ? (
          <p className="offer-timeline-empty">{tr('Empty')}</p>
        ) : null}

        {shown.length > 0 ? (
          <div className="offer-timeline-grid">
            {/* The names stay at the left while the timeline is dragged */}
            <div className="offer-timeline-names">
              <div className="offer-timeline-corner">
                {tr('Offer')}
                <span>{tr('Offers', { count: shown.length })}</span>
              </div>
              {groups.map((group) => (
                <React.Fragment key={group.key}>
                  {group.label !== null ? (
                    <div className="offer-timeline-group">
                      {group.label}
                      <span>
                        {hoursText(
                          group.offers.reduce(
                            (sum, offer) => sum + (offer.hours ?? 0),
                            0
                          )
                        )}
                      </span>
                    </div>
                  ) : null}
                  {group.offers.map((offer) => (
                    <button
                      className="offer-timeline-name"
                      key={offer.id}
                      onClick={() => open(offer)}
                      title={titleOf(offer)}
                      type="button"
                    >
                      {offer.ndaRestricted
                        ? t(`${translations.offers}:nda.Protected`)
                        : offer.targetName}
                    </button>
                  ))}
                </React.Fragment>
              ))}
            </div>

            {/* Dragged with the mouse; the offers are opened from their names and bars */}
            <div
              className="offer-timeline-scroller"
              onMouseDown={onMouseDown}
              ref={scroller}
              role="presentation"
            >
              <div className="offer-timeline-canvas" style={{ width }}>
                <div className="offer-timeline-scale">
                  <div className="scale-upper">
                    {upper.map((mark) => (
                      <span key={mark.day} style={{ left: x(mark.day) }}>
                        {mark.label}
                      </span>
                    ))}
                  </div>
                  <div className="scale-lower">
                    {lower.map((mark) => (
                      <span key={mark.day} style={{ left: x(mark.day) }}>
                        {mark.label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* The lines of the weeks or months and today */}
                <div className="offer-timeline-lines">
                  {lower.map((mark) => (
                    <i key={mark.day} style={{ left: x(mark.day) }} />
                  ))}
                  <b style={{ left: x(todayDay) + dayWidth / 2 }} />
                </div>

                {groups.map((group) => (
                  <React.Fragment key={group.key}>
                    {group.label !== null ? (
                      <div className="offer-timeline-group" />
                    ) : null}
                    {group.offers.map((offer) => (
                      <div className="offer-timeline-row" key={offer.id}>
                        {offer.contractFrom !== null &&
                        offer.contractTo !== null
                          ? renderBar(
                              offer,
                              offer.contractFrom,
                              offer.contractTo
                            )
                          : null}
                        {offer.siteFrom !== null ? (
                          <i
                            className="offer-timeline-point site-start"
                            style={{ left: x(offer.siteFrom) + dayWidth / 2 }}
                            title={`${tr('Site starts')}: ${formatDay(
                              offer.siteFrom
                            )}`}
                          />
                        ) : null}
                        {offer.siteTo !== null ? (
                          <i
                            className="offer-timeline-point site-end"
                            style={{ left: x(offer.siteTo) + dayWidth / 2 }}
                            title={`${tr('Site ends')}: ${formatDay(
                              offer.siteTo
                            )}`}
                          />
                        ) : null}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </PageContainer>
  );
};

export default OfferTimeline;
